import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Send, Sparkles, X, Loader2 } from "lucide-react";

// Minimal, safe markdown renderer for AI tutor replies
function renderTutorMarkdown(md) {
  if (!md) return "";
  let html = md;
  html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  html = html.replace(/^### (.*)$/gm, "<h4 class='font-serif text-base text-[var(--dw-navy)] mt-3 mb-1.5'>$1</h4>");
  html = html.replace(/^## (.*)$/gm, "<h3 class='font-serif text-lg text-[var(--dw-navy)] mt-3 mb-1.5'>$1</h3>");
  html = html.replace(/^# (.*)$/gm, "<h2 class='font-serif text-xl text-[var(--dw-navy)] mt-3 mb-2'>$1</h2>");
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong class='text-[var(--dw-navy)]'>$1</strong>");
  html = html.replace(/`([^`]+)`/g, "<code class='bg-[var(--dw-navy)]/8 px-1.5 py-0.5 rounded text-[0.85em] font-mono-plex'>$1</code>");
  // Tables
  html = html.replace(/((?:^\|.*\|\s*\n)+)/gm, (block) => {
    const rows = block.trim().split("\n").map((r) => r.trim());
    if (rows.length < 2) return block;
    const header = rows[0].slice(1, -1).split("|").map((c) => c.trim());
    const body = rows.slice(2).map((r) => r.slice(1, -1).split("|").map((c) => c.trim()));
    const th = header.map((c) => `<th class='border border-[var(--dw-navy)]/15 px-2 py-1 bg-[var(--dw-navy)]/5 text-left'>${c}</th>`).join("");
    const bd = body.map((row) => `<tr>${row.map((c) => `<td class='border border-[var(--dw-navy)]/15 px-2 py-1'>${c}</td>`).join("")}</tr>`).join("");
    return `<table class='w-full my-2 text-xs border-collapse'><thead><tr>${th}</tr></thead><tbody>${bd}</tbody></table>`;
  });
  html = html.replace(/^(?:- |\* )(.*)$/gm, "<li>$1</li>");
  html = html.replace(/^(?:\d+\. )(.*)$/gm, "<li>$1</li>");
  html = html.replace(/(<li>[\s\S]*?<\/li>)(?=(?:\n(?!<li>))|$)/g, (m) => `<ul class='list-disc pl-5 my-2 space-y-1'>${m}</ul>`);
  html = html.split(/\n{2,}/).map((chunk) => {
    if (/^<(h\d|ul|ol|table|pre|blockquote)/.test(chunk.trim())) return chunk;
    return `<p class='my-2'>${chunk.replace(/\n/g, "<br/>")}</p>`;
  }).join("\n");
  return html;
}

/**
 * TutorPanel can render as a full-page tutor (default) or a right-side overlay drawer.
 * Props:
 *   initialContext: optional string passed with the first message
 *   onClose: if present, renders drawer with a close button
 */
export default function TutorPanel({ initialContext, onClose }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: initialContext
        ? "I've got the current question in view. What would you like me to explain — the regulation, the calculation, or why a specific choice is wrong?"
        : "Hello — I'm your DW Training Academy compliance tutor. Ask me anything about RESPA, TILA, TRID, FHA/VA, ARMs, DTI math, ethics, or fraud red flags. I'll cite the regulation.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  async function send(e) {
    e?.preventDefault?.();
    if (!input.trim() || sending) return;
    const userMsg = { role: "user", text: input };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/tutor/chat", {
        message: userMsg.text,
        session_id: sessionId,
        context: messages.length === 1 ? initialContext : null,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry — the tutor is unavailable right now. Please try again." }]);
    } finally {
      setSending(false);
    }
  }

  const isDrawer = !!onClose;

  const suggestions = [
    "Explain RESPA Section 8 kickback rules with examples.",
    "How do I calculate per-diem interest? Show me on $350k @ 6.25%.",
    "When exactly must a SAR be filed with FinCEN?",
    "What's the difference between HCLTV, CLTV, and LTV?",
  ];

  return (
    <div className={isDrawer
      ? "fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end animate-in fade-in duration-200"
      : "min-h-[calc(100vh-2rem)]"
    } onClick={isDrawer ? onClose : undefined}>
      <div
        onClick={(e) => e.stopPropagation()}
        className={isDrawer
          ? "bg-[var(--dw-parchment)] w-full max-w-xl h-full flex flex-col border-l border-[var(--dw-navy)]/10 shadow-2xl"
          : "flex flex-col h-[calc(100vh-2rem)] max-w-3xl mx-auto"
        }
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 ${isDrawer ? "border-b border-[var(--dw-navy)]/10 bg-white" : ""}`}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-[var(--dw-navy)] rounded-sm">
              <Sparkles className="w-4 h-4 text-[var(--dw-gold)]" strokeWidth={1.5} />
            </div>
            <div>
              <div className="overline">AI Compliance Tutor</div>
              <div className="font-serif text-lg text-[var(--dw-navy)] leading-tight">Claude Sonnet 4.5</div>
            </div>
          </div>
          {isDrawer && (
            <button onClick={onClose} data-testid="close-tutor" className="text-neutral-500 hover:text-[var(--dw-navy)]">
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-4" data-testid="tutor-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-4 py-3 rounded-sm text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-[var(--dw-navy)] text-[var(--dw-parchment)] whitespace-pre-wrap"
                  : "bg-white border border-[var(--dw-navy)]/10 text-[var(--dw-ink)]"
              }`}>
                {m.role === "user"
                  ? m.text
                  : <div dangerouslySetInnerHTML={{ __html: renderTutorMarkdown(m.text) }} />}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white border border-[var(--dw-navy)]/10 px-4 py-3 rounded-sm text-sm text-neutral-500 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Reasoning…
              </div>
            </div>
          )}

          {messages.length === 1 && !isDrawer && (
            <div className="pt-4">
              <div className="overline mb-3">Try asking</div>
              <div className="grid gap-2">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => { setInput(s); }}
                    data-testid={`suggestion-${i}`}
                    className="text-left text-sm px-4 py-3 bg-white border border-[var(--dw-navy)]/10 rounded-sm hover:border-[var(--dw-gold)] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <form onSubmit={send} className="border-t border-[var(--dw-navy)]/10 p-4 bg-white">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              rows={2}
              placeholder="Ask about any regulation, calculation, or wrong answer…"
              data-testid="tutor-input"
              className="flex-1 border border-[var(--dw-navy)]/15 rounded-sm px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[var(--dw-gold)]/40 focus:border-[var(--dw-navy)]/40"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="tutor-send"
              className="dw-btn-primary rounded-sm px-4 py-2.5 text-sm flex items-center gap-1.5 disabled:opacity-40 shrink-0"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
